import type React from "react";
import { useCallback, useState } from "react";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";

interface UseGraphSelectionReturn {
	selectedNodeId: string | null;
	selectedEdgeId: string | null;
	isEditing: boolean;
	setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
	setSelectedEdgeId: React.Dispatch<React.SetStateAction<string | null>>;
	setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
	handleNodeClick: (node: VisualNode) => void;
	handleEdgeClick: (edge: VisualEdge) => void;
	handleBackgroundClick: () => void;
	handleGraphSelect: (
		graphId: string,
		setSelectedGraphId: (graphId: string) => void,
		closeDropdown: () => void,
	) => void;
	handleToggleEditing: () => void;
	clearSelection: () => void;
}

export const useGraphSelection = (): UseGraphSelectionReturn => {
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);

	const clearSelection = useCallback(() => {
		setSelectedNodeId(null);
		setSelectedEdgeId(null);
	}, []);

	const handleNodeClick = useCallback((node: VisualNode) => {
		setSelectedNodeId((prevId) => (node.id === prevId ? null : node.id));
		setSelectedEdgeId(null);
	}, []);

	const handleEdgeClick = useCallback((edge: VisualEdge) => {
		setSelectedEdgeId((prevId) => (edge.id === prevId ? null : edge.id));
		setSelectedNodeId(null);
	}, []);

	const handleBackgroundClick = useCallback(() => {
		clearSelection();
	}, [clearSelection]);

	const handleGraphSelect = useCallback(
		(graphId: string, setSelectedGraphId: (graphId: string) => void, closeDropdown: () => void) => {
			setSelectedGraphId(graphId);
			closeDropdown();
			clearSelection();
			setIsEditing(false);
		},
		[clearSelection],
	);

	const handleToggleEditing = useCallback(() => {
		setIsEditing((prev) => {
			if (prev) {
				setSelectedNodeId(null);
			}
			return !prev;
		});
	}, []);

	return {
		selectedNodeId,
		selectedEdgeId,
		isEditing,
		setSelectedNodeId,
		setSelectedEdgeId,
		setIsEditing,
		handleNodeClick,
		handleEdgeClick,
		handleBackgroundClick,
		handleGraphSelect,
		handleToggleEditing,
		clearSelection,
	};
};

export default useGraphSelection;
